class PromoBannerSlider {
  constructor(section) {
    this.section = section;
    this.wrapper = section.querySelector('.promo-banner-wrapper');
    this.slides = section.querySelectorAll('.promo-banner-slide');
    this.prevBtn = section.querySelector('.slider-prev');
    this.nextBtn = section.querySelector('.slider-next');
    this.paginationContainer = section.querySelector('.slider-pagination');
    
    this.currentIndex = 0;
    this.slideCount = this.slides.length;
    
    // Only initialize if there's more than one slide
    if (this.slideCount <= 1) {
      if (this.paginationContainer) this.paginationContainer.style.display = 'none';
      if (this.prevBtn && this.nextBtn) {
        this.prevBtn.style.display = 'none';
        this.nextBtn.style.display = 'none';
      }
      return;
    }
    
    this.autoplay = this.wrapper.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.wrapper.dataset.autoplaySpeed) || 5000;
    this.effect = this.wrapper.dataset.effect || 'slide';
    this.infinite = this.wrapper.dataset.infinite === 'true';
    
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    this.init();
  }
  
  init() {
    // Set initial active slide
    this.slides[0].classList.add('active');
    
    // Add effect class
    if (this.effect === 'fade') {
      this.wrapper.classList.add('fade-effect');
    }
    
    // Create pagination
    if (this.paginationContainer) {
      this.createPagination();
    }
    
    // Add event listeners
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
    
    // Touch events for mobile
    this.addTouchSupport();
    
    // Start autoplay
    if (this.autoplay) {
      this.startAutoplay();
    }
    
    // Pause autoplay on hover/touch
    this.section.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.section.addEventListener('mouseleave', () => this.startAutoplay());
    this.section.addEventListener('touchstart', () => this.pauseAutoplay());
    this.section.addEventListener('touchend', () => this.startAutoplay());
    
    // Keyboard navigation
    this.section.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });
  }
  
  createPagination() {
    for (let i = 0; i < this.slideCount; i++) {
      const dot = document.createElement('button');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === 0) dot.classList.add('active');
      
      dot.addEventListener('click', () => this.goToSlide(i));
      this.paginationContainer.appendChild(dot);
    }
  }
  
  updatePagination() {
    const dots = this.paginationContainer?.children;
    if (dots) {
      Array.from(dots).forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentIndex);
      });
    }
  }
  
  goToSlide(index) {
    if (index < 0 || index >= this.slideCount) return;
    
    if (this.effect === 'slide') {
      const slideWidth = this.slides[0].offsetWidth;
      this.wrapper.style.transform = `translateX(-${index * slideWidth}px)`;
    } else {
      // Fade effect
      this.slides.forEach(slide => slide.classList.remove('active'));
      this.slides[index].classList.add('active');
    }
    
    this.currentIndex = index;
    this.updatePagination();
    this.updateAriaAttributes();
  }
  
  next() {
    if (this.currentIndex < this.slideCount - 1) {
      this.goToSlide(this.currentIndex + 1);
    } else if (this.infinite) {
      this.goToSlide(0);
    }
  }
  
  prev() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    } else if (this.infinite) {
      this.goToSlide(this.slideCount - 1);
    }
  }
  
  addTouchSupport() {
    this.wrapper.addEventListener('touchstart', (e) => {
      this.touchStartX = e.touches[0].clientX;
    }, { passive: true });
    
    this.wrapper.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].clientX;
      this.handleSwipe();
    }, { passive: true });
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchEndX - this.touchStartX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.prev();
      } else {
        this.next();
      }
    }
  }
  
  startAutoplay() {
    if (!this.autoplay) return;
    this.pauseAutoplay();
    this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
  }
  
  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  updateAriaAttributes() {
    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index !== this.currentIndex);
    });
  }
  
  // Handle window resize
  handleResize() {
    if (this.effect === 'slide') {
      this.goToSlide(this.currentIndex);
    }
  }
}

// Initialize sliders when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.promo-banner-section');
  sliders.forEach(slider => {
    slider._slider = new PromoBannerSlider(slider);
  });
});

// Handle resize events
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    document.querySelectorAll('.promo-banner-section').forEach(slider => {
      if (slider._slider) {
        slider._slider.handleResize();
      }
    });
  }, 250);
});

// Handle dynamic section loading (for Shopify editor)
document.addEventListener('shopify:section:load', (event) => {
  const slider = event.target.querySelector('.promo-banner-section');
  if (slider && !slider._slider) {
    slider._slider = new PromoBannerSlider(slider);
  }
});

// Handle section unload (cleanup)
document.addEventListener('shopify:section:unload', (event) => {
  const slider = event.target.querySelector('.promo-banner-section');
  if (slider && slider._slider) {
    if (slider._slider.autoplayTimer) {
      clearInterval(slider._slider.autoplayTimer);
    }
    delete slider._slider;
  }
});